import {
  useCanEditGovernanceDomain,
  useGovernanceAccess,
} from '@/hooks/useGovernanceAccess';
import { useCanEditAllGovernanceDomains } from '@http/queries/useFetchPolicyDomainAccess';
import type { PolicyResponse } from '@http/queries/useFetchPolicies';
import { canEditPolicy } from './policyAccess';

/**
 * Governance rights are granted per domain, so being able to edit *some*
 * policy does not mean being able to edit *this* policy: that requires
 * Governance access to every domain the policy covers.
 *
 * `canEditPolicy` denies while the rights are still loading, so callers must
 * wait for `isPending` — acting earlier bounces users who do have access.
 */
export function usePolicyEditAccess() {
  const governanceAccess = useGovernanceAccess();
  const canEditDomain = useCanEditGovernanceDomain();
  const { canEditAllDomains, isPending: isDomainAccessPending } =
    useCanEditAllGovernanceDomains();

  return {
    isPending: governanceAccess.isLoading || isDomainAccessPending,
    canEditPolicy: (
      policy: Pick<PolicyResponse, 'domainScope' | 'auditEntryIds'>,
    ) => canEditPolicy({ policy, canEditAllDomains, canEditDomain }),
  };
}
